import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { BrandProvider } from "./contexts/BrandContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import App from "@/App";
import AuthPage from "./pages/AuthPage";
import AuthCallback from "./pages/AuthCallback";
import DashboardPage from "./pages/DashboardPage";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import PluginPage from "./pages/PluginPage";
import LocalSEOPage from "./pages/LocalSEOPage";
import LocalSEOBusinessPage from "./pages/LocalSEOBusinessPage";
import BrandMonitorPage from "./pages/BrandMonitorPage";
import AISimulatorPage from "./pages/AISimulatorPage";
import ProductPage from "./pages/ProductPage";
import FixMySEOPage from "./pages/FixMySEOPage";
import AiSnapshotLandingPage from "./pages/AiSnapshotLandingPage";
import BenchmarkLandingPage from "./pages/BenchmarkLandingPage";
import BenchmarkSamplePage from "./pages/BenchmarkSamplePage";
import SeoCommandWaitlistPage from "./pages/SeoCommandWaitlistPage";
import CityAiSeoPage from "./pages/CityAiSeoPage";
import FirmAiSeoPage from "./pages/FirmAiSeoPage";
import CompareSpaceSolutionsPage from "./pages/CompareSpaceSolutionsPage";
import CompareMarketingMinerPage from "./pages/CompareMarketingMinerPage";
import CompareCollabimPage from "./pages/CompareCollabimPage";
import CompareSemrushPage from "./pages/CompareSemrushPage";
import CompareMangoolsPage from "./pages/CompareMangoolsPage";
import CompareHubPage from "./pages/CompareHubPage";
import NotFoundPage from "./pages/NotFoundPage";
import LangRedirectOrNotFound from "./pages/LangRedirectOrNotFound";
import AttributionPage from "./pages/AttributionPage";
import LegalPage from "./pages/LegalPage";
import ResultsFunnelPage from "./pages/ResultsFunnelPage";
import CookieAuditPage from "./pages/CookieAuditPage";
import ComparisonPage from "./pages/ComparisonPage";
import AIStrategyPage from "./pages/AIStrategyPage";
import LocalizedLandingPage from "./pages/LocalizedLandingPage";
import BlogPacksPage from "./pages/BlogPacksPage";
import LegalCenterPage from "./pages/LegalCenterPage";
import LegalToolsDirectoryPage from "./pages/LegalToolsDirectoryPage";
import AdsCreatorLanding from "./ads-creator/AdsCreatorLanding";
import TemplateGallery from "./ads-creator/TemplateGallery";
import AdsEditor from "./ads-creator/Editor";
import MyAds from "./ads-creator/MyAds";
import CouponRedeemLanding from "./ads-creator/CouponRedeemLanding";
import CommunityLeaderboard from "./ads-creator/CommunityLeaderboard";
import InstantCampaign from "./ads-creator/InstantCampaign";
import PhotoStart from "./ads-creator/PhotoStart";
import EditorShowcasePage from "./pages/EditorShowcasePage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import CompetitorSpyPage from "./pages/CompetitorSpyPage";
import SEOAutopilotPage from "./pages/SEOAutopilotPage";
import BlogAgentPage from "./pages/BlogAgentPage";
import AIIntelligencePage from "./pages/AIIntelligencePage";
import AdsPreviewPage from "./pages/AdsPreviewPage";
import RevenueLeakPage from "./pages/RevenueLeakPage";
import SystemFunnelPage, { SystemHub } from "./pages/SystemFunnelPage";
import ToolsHubPage from "./pages/ToolsHubPage";
import ToolFunnelPage from "./pages/ToolFunnelPage";
import LifetimeDealPage from "./pages/LifetimeDealPage";
import ServicesHubPage from "./pages/ServicesHubPage";
import PersonaLandingPage from "./pages/PersonaLandingPage";
import SecurityMonitorPage from "./pages/SecurityMonitorPage";
import FunnelLandingPage, { AllInOfferPage } from "./pages/FunnelLandingPage";
import CongratsFunnelPage from "./pages/CongratsFunnelPage";
import ImprovementsLeaderboardPage from "./pages/ImprovementsLeaderboardPage";
import WebDesignFunnelPage from "./pages/WebDesignFunnelPage";
import WebProposalPage from "./pages/WebProposalPage";
import { AllInBanner } from "./components/common/AllInBanner";
import { useBehaviorTracker } from "./hooks/useBehaviorTracker";
import PreviewPage from "./pages/PreviewPage";
import AffiliatePage from "./pages/AffiliatePage";
import AffiliateRedirect from "./pages/AffiliateRedirect";
import AffiliateKitPage from "./pages/AffiliateKitPage";
import WastedCalculatorPage from "./pages/WastedCalculatorPage";
import PricingPage from "./pages/PricingPage";
import UpgradeRedirectPage from "./pages/UpgradeRedirectPage";
import EshopScannerPage from "./pages/EshopScannerPage";
import AgencyPortalPage from "./pages/AgencyPortalPage";
import AgencySharePage from "./pages/AgencySharePage";
import AgentOsAgencyLandingPage from "./pages/AgentOsAgencyLandingPage";
import AgentOsEshopLandingPage from "./pages/AgentOsEshopLandingPage";
import AgentOsDemoReviewReply from "./pages/AgentOsDemoReviewReply";
import AgentOsDemoProductDesc from "./pages/AgentOsDemoProductDesc";
import AgentOsDemoProductImporter from "./pages/AgentOsDemoProductImporter";
import AgentOsDemoSalesHunter from "./pages/AgentOsDemoSalesHunter";
import AgentOsDemoManager from "./pages/AgentOsDemoManager";
import RentAnAgentFunnel from "./pages/RentAnAgentFunnel";
import AgentMatchScan from "./pages/AgentMatchScan";
import AdAgentVideo from "./pages/AdAgentVideo";
import AgentLandingPage from "./pages/AgentLandingPage";
import HeroAgentFunnel from "./pages/HeroAgentFunnel";
import SmartFunnelRouter from "./pages/SmartFunnelRouter";
import AgentCompatScannerPage from "./pages/AgentCompatScannerPage";
import AgentStatsPage from "./pages/AgentStatsPage";
import AgentLandingPagePlugin from "./pages/AgentLandingPagePlugin";
import AgentPluginFunnel from "./pages/AgentPluginFunnel";
import SeoIndexerConnectPage from "./pages/SeoIndexerConnectPage";
import AIConciergeWidget from "./components/AIConciergeWidget";
import AgentOsDemoCartRecovery from "./pages/AgentOsDemoCartRecovery";
import AgentOsDemoNewsletter from "./pages/AgentOsDemoNewsletter";
import AgentOsLeaderboard from "./pages/AgentOsLeaderboard";
import AgentOsDemoHelpdesk from "./pages/AgentOsDemoHelpdesk";
import AgentOsDemoGdprWatch from "./pages/AgentOsDemoGdprWatch";
import AgentOsDemoSocialMedia from "./pages/AgentOsDemoSocialMedia";
import AgentOsDemoPricingIntel from "./pages/AgentOsDemoPricingIntel";
import AgentOsDemoCustomerOnboarding from "./pages/AgentOsDemoCustomerOnboarding";
import AgentOsDemoReviewModerator from "./pages/AgentOsDemoReviewModerator";
import AgentOsDemoBehaviorAnalytics from "./pages/AgentOsDemoBehaviorAnalytics";
import AgentOsOnboardingWizard from "./pages/AgentOsOnboardingWizard";
import AgentOsWorkspaceDashboard from "./pages/AgentOsWorkspaceDashboard";
import AgentOsSharePage from "./pages/AgentOsSharePage";
import DiagnosticFunnelPage from "./pages/DiagnosticFunnelPage";
import ManifestPage from "./pages/ManifestPage";
import ChallengePage from "./pages/ChallengePage";
import RecapPage from "./pages/RecapPage";
import AuditPage from "./pages/AuditPage";
import { StickyFreeAnalysisCTA } from "./components/StickyFreeAnalysisCTA";
import { WelcomeBanner } from "./components/WelcomeBanner";
import PublicCampaign from "./ads-creator/PublicCampaign";
import PublicAchievement from "./ads-creator/PublicAchievement";
import { ImpersonationBanner } from "./components/admin/ImpersonationBanner";
import { HelmetProvider } from "react-helmet-async";
import StudioCreate from "./studio/StudioCreate";
import StudioMore from "./studio/StudioMore";
import StudioTeam from "./studio/StudioTeam";
import PwaInstallPrompt from "./studio/PwaInstallPrompt";
import PushPermissionPrompt from "./components/PushPermissionPrompt";
import CookieConsentBanner from "./components/CookieConsentBanner";
import { registerPwa } from "./studio/pwa";
import { initAnalytics } from "./lib/analytics";
import { useTrackPageView } from "./lib/metaPixel";
import { ExitIntentVideoPopup } from "./components/common/ExitIntentVideoPopup";
import { useLanguage } from "./contexts/LanguageContext";

// Kick off PWA at module-load time. Single-shot, safe in dev & prod.
registerPwa();
// GA4 init (no-op when REACT_APP_GA4_MEASUREMENT_ID is not set).
initAnalytics();

function GlobalExitVideoPopup() {
  const { language } = useLanguage();
  return <ExitIntentVideoPopup language={language || "sk"} />;
}

function AppRouter() {
  const location = useLocation();
  useTrackPageView();
  useBehaviorTracker();
  // Check URL fragment for session_id (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <>
      <ImpersonationBanner />
      <AllInBanner />
      <WelcomeBanner />
      <PwaInstallPrompt />
      <PushPermissionPrompt />
      <CookieConsentBanner />
      <GlobalExitVideoPopup />
      <Routes>
      <Route path="/" element={<App />} />
      <Route path="/agent-compat" element={<AgentCompatScannerPage />} />
      <Route path="/plugin/agent/:slug" element={<AgentLandingPagePlugin />} />
      <Route path="/plugin/funnel" element={<AgentPluginFunnel />} />
      <Route path="/agents/seo-indexer/connect" element={<SeoIndexerConnectPage />} />
      <Route path="/dashboard/agent/:slug" element={<AgentStatsPage />} />
      <Route path="/login" element={<AuthPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/dashboard" element={<DashboardPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/plugin" element={<PluginPage />} />
      <Route path="/seo/:lang" element={<LocalSEOPage />} />
      <Route path="/seo/:lang/:city" element={<LocalSEOPage />} />
      <Route path="/seo/:lang/:city/:category" element={<LocalSEOBusinessPage />} />
      <Route path="/brand-monitor" element={<BrandMonitorPage />} />
      <Route path="/ai-simulator" element={<AISimulatorPage />} />
      <Route path="/products/:productId" element={<ProductPage />} />
      <Route path="/products/:productId/:region" element={<ProductPage />} />
      <Route path="/products/:productId/:region/:city" element={<ProductPage />} />
      <Route path="/fix-my-seo" element={<FixMySEOPage />} />
      <Route path="/snapshot" element={<AiSnapshotLandingPage />} />
      <Route path="/snapshot-9" element={<AiSnapshotLandingPage />} />
      <Route path="/benchmark" element={<BenchmarkLandingPage />} />
      <Route path="/benchmark/sample" element={<BenchmarkSamplePage />} />
      <Route path="/seo-command" element={<SeoCommandWaitlistPage />} />
      {/* V231.3 — Programmatic SEO: 153 SK+CZ city landing pages */}
      <Route path="/mesto/:slug/ai-seo-scan" element={<CityAiSeoPage />} />
      {/* V231.4 — Programmatic SEO: on-demand firm landings (ORSF-backed) */}
      <Route path="/firma/:ico/ai-seo-scan" element={<FirmAiSeoPage />} />
      {/* V232.5 — Compare hub (index of all comparator pages) */}
      <Route path="/compare" element={<CompareHubPage />} />
      {/* V232 — Competitor comparison SEO hack ("spacesolutions audit" ~500/mo) */}
      <Route path="/compare/toprankpulse-vs-space-solutions" element={<CompareSpaceSolutionsPage />} />
      {/* V232.3 — Marketing Miner comparison (~1 000 search/mo on marketingminer brand) */}
      <Route path="/compare/toprankpulse-vs-marketing-miner" element={<CompareMarketingMinerPage />} />
      {/* V232.4 — Collabim comparison (~800 search/mo CZ market, Collabim is a legit tool) */}
      <Route path="/compare/toprankpulse-vs-collabim" element={<CompareCollabimPage />} />
      {/* V232.6 — Semrush comparison (global giant, segmentation angle for SK/CZ + expats) */}
      <Route path="/compare/toprankpulse-vs-semrush" element={<CompareSemrushPage />} />
      {/* V232.7 — Mangools comparison (SK-born tool, respectful compliment angle) */}
      <Route path="/compare/toprankpulse-vs-mangools" element={<CompareMangoolsPage />} />
      <Route path="/attribution" element={<AttributionPage />} />
      <Route path="/cookie-audit" element={<CookieAuditPage />} />
      <Route path="/strategy" element={<AIStrategyPage />} />
      <Route path="/strategy/:shareId" element={<AIStrategyPage />} />
      <Route path="/results/:analysisId" element={<ResultsFunnelPage />} />
      <Route path="/privacy" element={<LegalPage />} />
      <Route path="/terms" element={<LegalPage />} />
      <Route path="/gdpr" element={<LegalPage />} />
      <Route path="/cookies" element={<LegalPage />} />
      <Route path="/vs/:competitor" element={<ComparisonPage />} />
      <Route path="/blog-packs" element={<BlogPacksPage />} />
      <Route path="/legal-center" element={<LegalCenterPage />} />
      <Route path="/legal-tools" element={<LegalToolsDirectoryPage />} />
      <Route path="/ads-creator" element={<AdsCreatorLanding />} />
      <Route path="/ads-creator/templates" element={<TemplateGallery />} />
      <Route path="/ads-creator/editor" element={<AdsEditor />} />
      <Route path="/ads-creator/editor/:projectId" element={<AdsEditor />} />
      <Route path="/ads-creator/my-ads" element={<MyAds />} />
      <Route path="/redeem" element={<CouponRedeemLanding />} />
      <Route path="/redeem/:code" element={<CouponRedeemLanding />} />
      <Route path="/community/top-sharers" element={<CommunityLeaderboard />} />
      <Route path="/studio" element={<StudioCreate />} />
      <Route path="/studio/create" element={<StudioCreate />} />
      <Route path="/studio/create/:type" element={<StudioCreate />} />
      <Route path="/studio/more" element={<StudioMore />} />
      <Route path="/studio/team" element={<StudioTeam />} />
      <Route path="/team/join/:code" element={<StudioTeam />} />
      <Route path="/ads-creator/instant-campaign" element={<InstantCampaign />} />
      <Route path="/ads-creator/photo-start" element={<PhotoStart />} />
      <Route path="/editor" element={<EditorShowcasePage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      <Route path="/competitor-spy" element={<CompetitorSpyPage />} />
      <Route path="/seo-autopilot" element={<SEOAutopilotPage />} />
      <Route path="/blog-agent" element={<BlogAgentPage />} />
      <Route path="/ai-intelligence" element={<AIIntelligencePage />} />
      <Route path="/free-ads-preview" element={<AdsPreviewPage />} />
      <Route path="/revenue-leak" element={<RevenueLeakPage />} />
      <Route path="/system" element={<SystemHub />} />
      <Route path="/system/:slug" element={<SystemFunnelPage />} />
      <Route path="/tools" element={<ToolsHubPage />} />
      <Route path="/tools/:slug" element={<ToolFunnelPage />} />
      <Route path="/lifetime" element={<LifetimeDealPage />} />
      <Route path="/sluzby" element={<ServicesHubPage />} />
      <Route path="/pre-agentury" element={<PersonaLandingPage audience="agentury" />} />
      <Route path="/pre-agentury/:persona" element={<PersonaLandingPage audience="agentury" />} />
      <Route path="/pre-firmy" element={<PersonaLandingPage audience="firmy" />} />
      <Route path="/bezpecnost-webu" element={<SecurityMonitorPage />} />
      <Route path="/vsetko-v-jednom-2026" element={<AllInOfferPage />} />
      <Route path="/riesenia/:slug" element={<FunnelLandingPage />} />
      <Route path="/gratulujeme/:lang/:variant" element={<CongratsFunnelPage />} />
      <Route path="/rebricek-zlepseni" element={<ImprovementsLeaderboardPage lang="sk" />} />
      <Route path="/tvorba-webu/:lang/:variant" element={<WebDesignFunnelPage />} />
      <Route path="/navrh-webu/:token" element={<WebProposalPage />} />
      <Route path="/cz/zebricek-zlepseni" element={<ImprovementsLeaderboardPage lang="cs" />} />
      <Route path="/pre-firmy/:persona" element={<PersonaLandingPage audience="firmy" />} />
      <Route path="/services" element={<ServicesHubPage />} />
      <Route path="/preview/:domain" element={<PreviewPage />} />
      <Route path="/affiliate" element={<AffiliatePage />} />
      <Route path="/aff/:code" element={<AffiliateRedirect />} />
      <Route path="/r/:code" element={<AffiliateRedirect />} />
      <Route path="/affiliate-kit" element={<AffiliateKitPage />} />
      <Route path="/kalkulacka-stratenych-konverzii" element={<WastedCalculatorPage />} />
      <Route path="/kalkulacka" element={<WastedCalculatorPage />} />
      <Route path="/wasted-calculator" element={<WastedCalculatorPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/cennik" element={<PricingPage />} />
      <Route path="/upgrade" element={<UpgradeRedirectPage />} />
      <Route path="/eshop-scanner" element={<EshopScannerPage />} />
      <Route path="/diagnostika-eshopu" element={<DiagnosticFunnelPage />} />
      <Route path="/diagnostika" element={<DiagnosticFunnelPage />} />
      <Route path="/manifest" element={<ManifestPage />} />
      <Route path="/manifesto" element={<ManifestPage />} />
      <Route path="/vyzva-5-dni" element={<ChallengePage />} />
      <Route path="/5-day-challenge" element={<ChallengePage />} />
      <Route path="/challenge" element={<ChallengePage />} />
      <Route path="/recap/:token" element={<RecapPage />} />
      <Route path="/audit/:slug" element={<AuditPage />} />
      <Route path="/agency" element={<AgencyPortalPage />} />
      <Route path="/agency-share/:token" element={<AgencySharePage />} />
      <Route path="/agent-os" element={<AgentOsAgencyLandingPage />} />
      <Route path="/agent-os/agency" element={<AgentOsAgencyLandingPage />} />
      <Route path="/agent-os/eshop" element={<AgentOsEshopLandingPage />} />
      <Route path="/agent-os/demo" element={<AgentOsDemoReviewReply />} />
      <Route path="/agent-os/demo/review-reply" element={<AgentOsDemoReviewReply />} />
      <Route path="/agent-os/demo/product-desc" element={<AgentOsDemoProductDesc />} />
      <Route path="/agent-os/demo/cart-recovery" element={<AgentOsDemoCartRecovery />} />
      <Route path="/agent-os/demo/newsletter" element={<AgentOsDemoNewsletter />} />
      <Route path="/agent-os/leaderboard" element={<AgentOsLeaderboard />} />
      <Route path="/agenti/leaderboard" element={<AgentOsLeaderboard />} />
      <Route path="/agent-os/demo/helpdesk" element={<AgentOsDemoHelpdesk />} />
      <Route path="/agenti/demo/helpdesk" element={<AgentOsDemoHelpdesk />} />
      <Route path="/agent-os/demo/gdpr-watch" element={<AgentOsDemoGdprWatch />} />
      <Route path="/agenti/demo/gdpr-watch" element={<AgentOsDemoGdprWatch />} />
      <Route path="/agent-os/demo/social-media" element={<AgentOsDemoSocialMedia />} />
      <Route path="/agenti/demo/social-media" element={<AgentOsDemoSocialMedia />} />
      <Route path="/agent-os/demo/pricing-intel" element={<AgentOsDemoPricingIntel />} />
      <Route path="/agenti/demo/pricing-intel" element={<AgentOsDemoPricingIntel />} />
      <Route path="/agent-os/demo/customer-onboarding" element={<AgentOsDemoCustomerOnboarding />} />
      <Route path="/agenti/demo/customer-onboarding" element={<AgentOsDemoCustomerOnboarding />} />
      <Route path="/agent-os/demo/review-moderator" element={<AgentOsDemoReviewModerator />} />
      <Route path="/agenti/demo/review-moderator" element={<AgentOsDemoReviewModerator />} />
      <Route path="/agent-os/demo/behavior-analytics" element={<AgentOsDemoBehaviorAnalytics />} />
      <Route path="/agenti/demo/behavior-analytics" element={<AgentOsDemoBehaviorAnalytics />} />
      <Route path="/agent-os/demo/product-importer" element={<AgentOsDemoProductImporter />} />
      <Route path="/agenti/demo/product-importer" element={<AgentOsDemoProductImporter />} />
      <Route path="/agent-os/demo/sales-hunter" element={<AgentOsDemoSalesHunter />} />
      <Route path="/agenti/demo/sales-hunter" element={<AgentOsDemoSalesHunter />} />
      <Route path="/agenti/demo/obchodnik" element={<AgentOsDemoSalesHunter />} />
      <Route path="/agent-os/demo/competitive-manager" element={<AgentOsDemoManager />} />
      <Route path="/agenti/demo/competitive-manager" element={<AgentOsDemoManager />} />
      <Route path="/agenti/demo/manager" element={<AgentOsDemoManager />} />
      <Route path="/prenajmi-si-agenta" element={<RentAnAgentFunnel />} />
      <Route path="/rent-an-agent" element={<RentAnAgentFunnel />} />
      <Route path="/agent-match" element={<AgentMatchScan />} />
      <Route path="/agent-match/scan/:scanId" element={<AgentMatchScan />} />
      <Route path="/skenuj-agenta" element={<AgentMatchScan />} />
      <Route path="/scan-agents" element={<AgentMatchScan />} />
      <Route path="/reklama/ai-agenti" element={<AdAgentVideo />} />
      <Route path="/ad/ai-agents" element={<AdAgentVideo />} />
      <Route path="/stan-sa-hrdinom" element={<HeroAgentFunnel />} />
      <Route path="/become-hero" element={<HeroAgentFunnel />} />
      <Route path="/hero" element={<HeroAgentFunnel />} />
      <Route path="/start" element={<SmartFunnelRouter />} />
      <Route path="/zacni" element={<SmartFunnelRouter />} />
      <Route path="/agent/:slug" element={<AgentLandingPage />} />
      <Route path="/agenti/:slug" element={<AgentLandingPage />} />
      <Route path="/agent-os/onboard" element={<AgentOsOnboardingWizard />} />
      <Route path="/agent-os/onboarding" element={<AgentOsOnboardingWizard />} />
      <Route path="/agent-os/workspace/:workspaceId" element={<AgentOsWorkspaceDashboard />} />
      <Route path="/agent-os/workspace" element={<AgentOsWorkspaceDashboard />} />
      <Route path="/agent-os/share/:shareId" element={<AgentOsSharePage />} />
      <Route path="/agenti/share/:shareId" element={<AgentOsSharePage />} />
      <Route path="/agenti" element={<AgentOsEshopLandingPage />} />
      <Route path="/agenti/agentura" element={<AgentOsAgencyLandingPage />} />
      <Route path="/agenti/eshop" element={<AgentOsEshopLandingPage />} />
      <Route path="/agenti/demo" element={<AgentOsDemoReviewReply />} />
      <Route path="/agenti/demo/review-reply" element={<AgentOsDemoReviewReply />} />
      <Route path="/agenti/demo/product-desc" element={<AgentOsDemoProductDesc />} />
      <Route path="/agenti/demo/cart-recovery" element={<AgentOsDemoCartRecovery />} />
      <Route path="/agenti/demo/newsletter" element={<AgentOsDemoNewsletter />} />
      <Route path="/agenti/onboard" element={<AgentOsOnboardingWizard />} />
      <Route path="/c/:campaignId" element={<PublicCampaign />} />
      <Route path="/a/:hash/:achievementId" element={<PublicAchievement />} />
      <Route path="/privacy-generator" element={<LegalCenterPage />} />
      <Route path="/cookie-banner" element={<LegalCenterPage />} />
      <Route path="/impressum-generator" element={<LegalCenterPage />} />
      <Route path="/terms-generator" element={<LegalCenterPage />} />
      {/* Localized SEO landing variants (3 angles × 26 locales) */}
      <Route path="/:lang/ai-visibility" element={<LocalizedLandingPage variant="ai-visibility" />} />
      <Route path="/:lang/outrank" element={<LocalizedLandingPage variant="outrank" />} />
      <Route path="/:lang/free-scan" element={<LocalizedLandingPage variant="free-scan" />} />
      {/* V231 — Bare locale codes (/ar, /en, /es, /hi, /fi, /da, /pt...).
          Previously fell through to `*` → <App /> → soft 404. */}
      <Route path="/:lang" element={<LangRedirectOrNotFound />} />
      {/* V231 — Dedicated 404 for every unknown route (adds noindex meta). */}
      <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <StickyFreeAnalysisCTA />
    </>
  );
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <BrandProvider>
        <LanguageProvider>
          <AuthProvider>
            <ThemeProvider>
              <AppRouter />
            </ThemeProvider>
          </AuthProvider>
        </LanguageProvider>
      </BrandProvider>
    </BrowserRouter>
  </React.StrictMode>,
);
